import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import { Box, Grid, Typography, Button, Popover } from '@mui/material';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import AddIcon from '@mui/icons-material/Add';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import AssessmentRoundedIcon from '@mui/icons-material/AssessmentRounded';
import { toast } from '../../components/toast';
import { getEmployeeShifts } from '../../services/api';
import { getUserGroups } from '../../utils';
import { useAttendanceOperations } from '../../hooks/useAttendanceOperations';
import { attendanceValidationSchema, getInitialAttendanceFormData } from '../../utils/attendanceValidation';
import {
  AttendanceStatsCards,
  AttendanceFilters,
  AttendanceTable,
  AttendanceForm,
  AttendanceDeleteDialog,
  BatchAttendanceDialog,
  UploadAttendanceDialog,
  AttendanceReportDialog,
} from '../../components/attendance';
import { HRPageHeader } from '../../components/hr';
import { LightTooltip } from '../../components/custom-components/custom-tooltip';

const allowedGroups = ['admin', 'hr'];

export default function AttendanceTrackingPage() {
  const navigate = useNavigate();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogMode, setDialogMode] = useState('add');
  const [editingAttendance, setEditingAttendance] = useState(null);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [attendanceToDelete, setAttendanceToDelete] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [employeeFilter, setEmployeeFilter] = useState('All');
  const [departmentFilter, setDepartmentFilter] = useState('All');
  const [statusFilter, setStatusFilter] = useState('All');
  const [dateRange, setDateRange] = useState({
    startDate: null,
    endDate: null,
    preset: 'All',
  });
  const [openBatchDialog, setOpenBatchDialog] = useState(false);
  const [openUploadDialog, setOpenUploadDialog] = useState(false);
  const [openReportDialog, setOpenReportDialog] = useState(false);
  const [batchSelectedDate, setBatchSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [datePickerAnchor, setDatePickerAnchor] = useState(null);
  const [employeeShifts, setEmployeeShifts] = useState([]);
  const [isLoadingShifts, setIsLoadingShifts] = useState(false);

  const {
    isLoading,
    attendance,
    stats,
    totalCount,
    activeEmployees,
    filterOptions,
    loadAttendance,
    createAttendanceRecord,
    updateAttendanceRecord,
    deleteAttendanceRecord,
    batchCreateAttendanceRecords,
    uploadAttendanceCsvFile,
  } = useAttendanceOperations();

  const [formData, setFormData] = useState(getInitialAttendanceFormData());
  const [formErrors, setFormErrors] = useState({});

  useEffect(() => {
    const userGroups = getUserGroups();
    if (userGroups === null || !allowedGroups.some((group) => userGroups.includes(group))) {
      navigate('/dashboard', { replace: true });
    }
  }, [navigate]);

  useEffect(() => {
    loadAttendance(
      page,
      rowsPerPage,
      searchTerm,
      employeeFilter,
      departmentFilter,
      statusFilter,
      dateRange.startDate,
      dateRange.endDate
    );
  }, [
    page,
    rowsPerPage,
    searchTerm,
    employeeFilter,
    departmentFilter,
    statusFilter,
    dateRange.startDate,
    dateRange.endDate,
    loadAttendance,
  ]);

  useEffect(() => {
    const loadShiftOptions = async () => {
      setIsLoadingShifts(true);
      try {
        const response = await getEmployeeShifts(1, 1000);
        setEmployeeShifts(response?.data?.results || []);
      } catch (error) {
        console.error('Error loading employee shifts:', error);
        toast.error(error?.response?.data?.message || 'Failed to load employee shifts');
      } finally {
        setIsLoadingShifts(false);
      }
    };

    loadShiftOptions();
  }, []);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleOpenDialog = (mode, attendance = null) => {
    setDialogMode(mode);
    setEditingAttendance(attendance);
    if ((mode === 'edit' || mode === 'view') && attendance) {
      setFormData({
        employee: attendance.employee?.id ? attendance.employee.id.toString() : '',
        date: attendance.date || '',
        check_in_time: attendance.check_in_time || '',
        check_out_time: attendance.check_out_time || '',
        total_hours: attendance.total_hours || '',
        status: attendance.status || 'Present',
        remarks: attendance.remarks || '',
      });
    } else {
      setFormData(getInitialAttendanceFormData());
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingAttendance(null);
    setFormErrors({});
    setFormData(getInitialAttendanceFormData());
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
    if (formErrors[field]) {
      setFormErrors((prev) => ({ ...prev, [field]: '' }));
    }
  };

  const validateForm = async () => {
    try {
      await attendanceValidationSchema.validate(formData, { abortEarly: false });
      setFormErrors({});
      return true;
    } catch (error) {
      const errors = {};
      error.inner.forEach((err) => {
        if (err.path) {
          errors[err.path] = err.message;
        }
      });
      setFormErrors(errors);
      return false;
    }
  };

  const handleSubmit = async () => {
    const isValid = await validateForm();
    if (!isValid) {
      toast.error('Please fill in all required fields correctly');
      return;
    }

    const attendanceData = {
      employee_id: parseInt(formData.employee, 10),
      date: formData.date,
      check_in_time: formData.check_in_time || null,
      check_out_time: formData.check_out_time || null,
      status: formData.status,
      remarks: formData.remarks || null,
    };

    let success = false;
    if (dialogMode === 'edit' && editingAttendance) {
      success = await updateAttendanceRecord(editingAttendance.id, attendanceData);
    } else {
      success = await createAttendanceRecord(attendanceData);
    }

    if (success) {
      handleCloseDialog();
      loadAttendance(
        page,
        rowsPerPage,
        searchTerm,
        employeeFilter,
        departmentFilter,
        statusFilter,
        dateRange.startDate,
        dateRange.endDate
      );
    }
  };

  const handleDeleteAttendance = (attendance) => {
    setAttendanceToDelete(attendance);
    setOpenDeleteDialog(true);
  };

  const handleConfirmDelete = async () => {
    if (attendanceToDelete) {
      const success = await deleteAttendanceRecord(attendanceToDelete.id);
      if (success) {
        setOpenDeleteDialog(false);
        setAttendanceToDelete(null);
        loadAttendance(
          page,
          rowsPerPage,
          searchTerm,
          employeeFilter,
          departmentFilter,
          statusFilter,
          dateRange.startDate,
          dateRange.endDate
        );
      }
    }
  };

  const handleCancelDelete = () => {
    setOpenDeleteDialog(false);
    setAttendanceToDelete(null);
  };

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
    setPage(0);
  };

  const handleFilterChange = (filterType, value) => {
    console.log(filterType, value);
    switch (filterType) {
      case 'employee':
        setEmployeeFilter(value);
        break;
      case 'department':
        setDepartmentFilter(value);
        break;
      case 'status':
        setStatusFilter(value);
        break;
      default:
        break;
    }
    setPage(0);
  };

  const handleDateRangeChange = (newDateRange) => {
    setDateRange(newDateRange);
    setPage(0);
  };

  const handleClearFilters = () => {
    setSearchTerm('');
    setEmployeeFilter('All');
    setDepartmentFilter('All');
    setStatusFilter('All');
    setDateRange({
      startDate: null,
      endDate: null,
      preset: 'All',
    });
    setPage(0);
  };

  const handleOpenBatchDialog = () => {
    setOpenBatchDialog(true);
  };

  const handleCloseBatchDialog = () => {
    setOpenBatchDialog(false);
  };

  const handleOpenDatePicker = (event) => {
    setDatePickerAnchor(event.currentTarget);
  };

  const handleOpenUploadDialog = () => {
    setOpenUploadDialog(true);
  };

  const handleCloseUploadDialog = () => {
    setOpenUploadDialog(false);
  };

  const handleOpenReportDialog = () => {
    setOpenReportDialog(true);
  };

  const handleCloseReportDialog = () => {
    setOpenReportDialog(false);
  };

  const handleCloseDatePicker = () => {
    setDatePickerAnchor(null);
  };

  const handleDateChange = (newValue) => {
    if (!newValue) return;

    const formatted = dayjs(newValue).format('YYYY-MM-DD');
    const today = dayjs().format('YYYY-MM-DD');

    if (dayjs(formatted).isAfter(today)) {
      toast.error('Cannot generate attendance for future dates');
      return;
    }

    setBatchSelectedDate(formatted);
  };

  const handleGenerateForSelectedDate = () => {
    if (!batchSelectedDate) {
      toast.error('Select a date to generate attendance');
      return;
    }
    handleCloseDatePicker();
    handleOpenBatchDialog();
  };

  const handleBatchAttendanceSubmit = async (attendanceRecords) => {
    const success = await batchCreateAttendanceRecords(attendanceRecords);
    if (success) {
      loadAttendance(
        page,
        rowsPerPage,
        searchTerm,
        employeeFilter,
        departmentFilter,
        statusFilter,
        dateRange.startDate,
        dateRange.endDate
      );
    }
  };

  const handleUploadAttendanceSubmit = async (payload) => {
    const success = await uploadAttendanceCsvFile(payload);
    if (success) {
      loadAttendance(
        page,
        rowsPerPage,
        searchTerm,
        employeeFilter,
        departmentFilter,
        statusFilter,
        dateRange.startDate,
        dateRange.endDate
      );
    }
    return success;
  };

  return (
    <>
      <Helmet>
        <title>BillerBay Transmit | Attendance Tracking</title>
      </Helmet>

      <Grid container spacing={3}>
        <Grid item xs={12}>
          <HRPageHeader
            title="Attendance tracking"
            description="Review daily check-ins, upload CSV batches for bulk entry, and generate period reports. Use batch tools to mark attendance for multiple employees at once."
            actions={
              <>
              <LightTooltip title="Import attendance rows from a CSV file for the selected date.">
              <span>
              <Button
                variant="outlined"
                onClick={handleOpenUploadDialog}
                disabled={isLoading}
                startIcon={<UploadFileIcon />}
              >
                Upload Attendance CSV
              </Button>
              </span>
              </LightTooltip>
              <LightTooltip title="Build a printable or downloadable attendance report for a date range.">
              <span>
              <Button
                variant="outlined"
                onClick={handleOpenReportDialog}
                disabled={isLoading}
                startIcon={<AssessmentRoundedIcon />}
              >
                Generate Report
              </Button>
              </span>
              </LightTooltip>
              <LightTooltip title="Open batch attendance for a selected date.">
                <span>
                  <Button
                    variant="outlined"
                    onClick={handleOpenDatePicker}
                    disabled={isLoading}
                    startIcon={<CalendarTodayIcon />}
                  >
                    Generate batch attendance
                  </Button>
                </span>
              </LightTooltip>
              <LightTooltip title="Add a single attendance record for one employee.">
                <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpenDialog('add')}>
                  Add record
                </Button>
              </LightTooltip>
              </>
            }
          />
        </Grid>

        <Grid item xs={12}>
          <AttendanceStatsCards stats={stats} isLoading={isLoading} />
        </Grid>

        <Grid item xs={12}>
          <AttendanceFilters
            searchTerm={searchTerm}
            employeeFilter={employeeFilter}
            departmentFilter={departmentFilter}
            statusFilter={statusFilter}
            dateRange={dateRange}
            onSearchChange={handleSearchChange}
            onFilterChange={handleFilterChange}
            onDateRangeChange={handleDateRangeChange}
            onClearFilters={handleClearFilters}
            filterOptions={filterOptions}
          />
        </Grid>

        <Grid item xs={12}>
          <AttendanceTable
            attendance={attendance}
            isLoading={isLoading}
            page={page}
            rowsPerPage={rowsPerPage}
            totalCount={totalCount}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            onViewAttendance={(attendance) => handleOpenDialog('view', attendance)}
            onEditAttendance={(attendance) => handleOpenDialog('edit', attendance)}
            onDeleteAttendance={handleDeleteAttendance}
          />
        </Grid>
      </Grid>

      <AttendanceForm
        open={openDialog}
        mode={dialogMode}
        formData={formData}
        formErrors={formErrors}
        onClose={handleCloseDialog}
        onSubmit={handleSubmit}
        onInputChange={handleInputChange}
        initialEmployee={editingAttendance?.employee}
      />

      <AttendanceDeleteDialog
        open={openDeleteDialog}
        attendanceToDelete={attendanceToDelete}
        onCancel={handleCancelDelete}
        onConfirm={handleConfirmDelete}
      />

      <BatchAttendanceDialog
        open={openBatchDialog}
        onClose={handleCloseBatchDialog}
        onSubmit={handleBatchAttendanceSubmit}
        employees={activeEmployees}
        isLoading={isLoading}
        selectedDate={batchSelectedDate}
      />

      <UploadAttendanceDialog
        open={openUploadDialog}
        onClose={handleCloseUploadDialog}
        onSubmit={handleUploadAttendanceSubmit}
        shifts={employeeShifts}
        isLoadingShifts={isLoadingShifts}
      />

      <AttendanceReportDialog
        open={openReportDialog}
        onClose={handleCloseReportDialog}
        departmentOptions={filterOptions?.departments}
      />

      <Popover
        open={Boolean(datePickerAnchor)}
        anchorEl={datePickerAnchor}
        onClose={handleCloseDatePicker}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
      >
        <Box p={2}>
          <Typography variant="subtitle2" gutterBottom>
            Select Date for Batch Attendance
          </Typography>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              label="Date"
              value={batchSelectedDate ? dayjs(batchSelectedDate) : null}
              onChange={handleDateChange}
              maxDate={dayjs()}
              slotProps={{
                textField: {
                  fullWidth: true,
                  size: 'small',
                  InputLabelProps: { shrink: true },
                },
              }}
            />
          </LocalizationProvider>
          <Box display="flex" justifyContent="flex-end" gap={1} mt={2}>
            <Button size="small" onClick={handleCloseDatePicker}>
              Cancel
            </Button>
            <Button size="small" variant="contained" onClick={handleGenerateForSelectedDate}>
              Generate
            </Button>
          </Box>
        </Box>
      </Popover>
    </>
  );
}
